/**
 * Firebase Auth + Firestore — enabled only when VITE_FIREBASE_* env vars are set.
 * When unconfigured, `isFirebaseConfigured` is false and the app falls back to
 * the local demo sign-in + backend SQLite / localStorage for history.
 */
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged as fbOnAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  type Auth,
  type User as FbUser,
} from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
};

export const isFirebaseConfigured = Boolean(cfg.apiKey && cfg.authDomain && cfg.projectId);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let firestore: Firestore | null = null;

if (isFirebaseConfigured) {
  app = initializeApp(cfg as Record<string, string>);
  auth = getAuth(app);
  firestore = getFirestore(app);
}

export function getFirebaseAuth(): Auth {
  if (!auth) throw new Error("Firebase not configured");
  return auth;
}

export function getDb(): Firestore {
  if (!firestore) throw new Error("Firebase not configured");
  return firestore;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
}

function toAuthUser(u: FbUser): AuthUser {
  return {
    id: u.uid,
    name: u.displayName ?? u.email?.split("@")[0] ?? "Student",
    email: u.email ?? "",
  };
}

export async function signInWithGoogle(): Promise<AuthUser> {
  const provider = new GoogleAuthProvider();
  const res = await signInWithPopup(getFirebaseAuth(), provider);
  return toAuthUser(res.user);
}

export async function signInWithEmail(email: string, password: string): Promise<AuthUser> {
  const a = getFirebaseAuth();
  try {
    const res = await signInWithEmailAndPassword(a, email, password);
    return toAuthUser(res.user);
  } catch {
    // First-time users: create the account.
    const res = await createUserWithEmailAndPassword(a, email, password);
    return toAuthUser(res.user);
  }
}

export async function firebaseSignOut(): Promise<void> {
  if (auth) await fbSignOut(auth);
}

export function onAuthUser(cb: (user: AuthUser | null) => void): () => void {
  if (!auth) return () => undefined;
  return fbOnAuthStateChanged(auth, (u) => cb(u ? toAuthUser(u) : null));
}
